// Step 3 — Content. Channel-aware. Renders an Offer block (when offer ≠ none),
// an Email content panel (when channels includes email), and a Network-upsell
// panel (when channels includes network-upsell). Stacked vertically.

import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Icon, Textinput, Typo, theme } from '../../../../ui';
import UpsellCardPreview from '../UpsellCardPreview';
import { CHANNEL, EMAIL_TEMPLATE, LIMITS, OFFER } from '../../constants';
import { CAMPAIGN_TEMPLATES, MOCK_COURSES_FOR_FILTER } from '../../mockData';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.NEU_100};

  &:first-of-type {
    border-top: 0;
    padding-top: 0;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;

  & > span:first-child {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.NEU_600};
  }
`;

const TextArea = styled.textarea`
  font-family: inherit;
  font-size: 14px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 8px;
  resize: vertical;
  min-height: 120px;
`;

const TemplateGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const TemplateCard = styled.button`
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px 12px;
  border-radius: 10px;
  background: ${({ $selected, theme }) => ($selected ? theme.colors.PRIMARY_LIGHT : theme.colors.NEU_50)};
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.colors.PRIMARY : 'transparent')};
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;

  &:hover {
    border-color: ${({ theme }) => theme.colors.PRIMARY};
  }
`;

const OfferBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${({ theme }) => theme.colors.NEU_50};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 10px;
  padding: 16px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  /* Each child field grows equally but won't shrink below 200px — the row wraps instead. */
  & > * {
    flex: 1 1 200px;
  }
`;

const FieldError = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.ERROR_RED};
`;

const TEMPLATE_VISUAL = {
  [EMAIL_TEMPLATE.PROMOTIONAL]: { icon: <Icon.Send size='small' />, label: 'Promotional' },
  [EMAIL_TEMPLATE.DISCOUNT]: { icon: <Icon.Coupon size='small' />, label: 'Discount' },
  [EMAIL_TEMPLATE.ANNOUNCEMENT]: { icon: <Icon.Info size='small' />, label: 'Announcement' },
};

const StepContent = ({ draft, errors = {}, onChange }) => {
  const { t } = useTranslation();
  const channels = draft?.channels ?? [];
  const offer = draft?.offer ?? OFFER.NONE;
  const variant = draft?.content?.variants?.[0] ?? {};
  const featured = draft?.content?.networkUpsell?.featuredCourseIds ?? [];

  const updateVariant = useCallback(
    (patch) => {
      const next = { ...(draft?.content ?? {}) };
      next.variants = [{ ...variant, ...patch }];
      onChange({ ...draft, content: next });
    },
    [draft, variant, onChange],
  );

  const updateNetworkUpsell = useCallback(
    (patch) => {
      onChange({
        ...draft,
        content: {
          ...(draft?.content ?? {}),
          networkUpsell: { ...(draft?.content?.networkUpsell ?? {}), ...patch },
        },
      });
    },
    [draft, onChange],
  );

  const updateOffer = useCallback(
    (patch) => {
      if (offer === OFFER.DISCOUNT) {
        const nextDiscount = { ...(draft?.discount ?? {}), ...patch };
        // Discount + network-upsell: featured courses always follow the scope so
        // the in-app card mirrors what the discount applies to. Empty scope
        // means store-wide → card just shows the offer badge.
        const nextContent =
          patch.scopeCourseIds !== undefined && channels.includes(CHANNEL.NETWORK_UPSELL)
            ? {
                ...(draft?.content ?? {}),
                networkUpsell: {
                  ...(draft?.content?.networkUpsell ?? {}),
                  featuredCourseIds: nextDiscount.scopeCourseIds ?? [],
                },
              }
            : draft?.content;
        onChange({ ...draft, discount: nextDiscount, content: nextContent });
      } else if (offer === OFFER.INVITATION) {
        const nextInvitation = { ...(draft?.invitation ?? {}), ...patch };
        // Invitation + network-upsell: the in-app card promotes the invited course.
        // Keep `networkUpsell.featuredCourseIds` in sync so the user doesn't have
        // to pick the same course twice.
        const nextContent =
          patch.courseId !== undefined && channels.includes(CHANNEL.NETWORK_UPSELL)
            ? {
                ...(draft?.content ?? {}),
                networkUpsell: {
                  ...(draft?.content?.networkUpsell ?? {}),
                  featuredCourseIds: nextInvitation.courseId ? [nextInvitation.courseId] : [],
                },
              }
            : draft?.content;
        onChange({ ...draft, invitation: nextInvitation, content: nextContent });
      }
    },
    [draft, offer, channels, onChange],
  );

  const toggleFeatured = (id) => {
    const set = new Set(featured);
    if (set.has(id)) set.delete(id);
    else if (set.size < LIMITS.FEATURED_COURSES_MAX) set.add(id);
    updateNetworkUpsell({ featuredCourseIds: [...set] });
  };

  const setTemplate = (id) => onChange({ ...draft, template: id });

  return (
    <Card>
      <Header>
        <Typo.ContentSmallHeader>{t('campaigns.content.title', 'Content')}</Typo.ContentSmallHeader>
        <Typo.SmallText color='textSecondary'>
          {t(
            'campaigns.content.description',
            'Author the email body and pick the featured courses for the in-app card. Each section maps to a channel you selected in step 1.',
          )}
        </Typo.SmallText>
      </Header>

      {offer === OFFER.DISCOUNT && (
        <Section>
          <SectionTitle>
            <Icon.Coupon size='small' />
            {t('campaigns.content.discountOffer', 'Discount offer')}
          </SectionTitle>
          <OfferBox>
            <Row $cols={3}>
              <Field>
                <span>{t('campaigns.content.code', 'Code')}</span>
                <Textinput value={draft?.discount?.code ?? ''} onChange={(e) => updateOffer({ code: e.target.value.toUpperCase() })} placeholder='SUMMER30' />
                {errors.discountCode && <FieldError>{errors.discountCode}</FieldError>}
              </Field>
              <Field>
                <span>{t('campaigns.content.percentOff', 'Percent off')}</span>
                <Textinput type='number' value={draft?.discount?.percentage ?? ''} onChange={(e) => updateOffer({ percentage: Number(e.target.value) || 0 })} />
                {errors.discountPercentage && <FieldError>{errors.discountPercentage}</FieldError>}
              </Field>
              <Field>
                <span>{t('campaigns.content.validUntil', 'Valid until')}</span>
                <Textinput
                  type='date'
                  value={draft?.discount?.validUntil?.slice(0, 10) ?? ''}
                  onChange={(e) => updateOffer({ validUntil: e.target.value ? new Date(e.target.value).toISOString() : null })}
                />
                {errors.discountValidity && <FieldError>{errors.discountValidity}</FieldError>}
              </Field>
            </Row>

            <Field>
              <span>{t('campaigns.content.discountScope', 'Courses the code is valid for')}</span>
              <Typo.SmallText color='textSecondary'>
                {t('campaigns.content.discountScopeHint', 'Leave empty to let the code apply to every course. Pick one or more to limit the scope.')}
              </Typo.SmallText>
              <Row>
                {MOCK_COURSES_FOR_FILTER.map((c) => {
                  const scope = draft?.discount?.scopeCourseIds ?? [];
                  const checked = scope.includes(c.id);
                  return (
                    <label key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type='checkbox'
                        checked={checked}
                        onChange={() => {
                          const set = new Set(scope);
                          if (set.has(c.id)) set.delete(c.id);
                          else set.add(c.id);
                          updateOffer({ scopeCourseIds: [...set] });
                        }}
                      />
                      {c.name}
                    </label>
                  );
                })}
              </Row>
            </Field>
          </OfferBox>
        </Section>
      )}

      {offer === OFFER.INVITATION && (
        <Section>
          <SectionTitle>
            <Icon.Gift size='small' />
            {t('campaigns.content.invitation', 'Invitation')}
          </SectionTitle>
          <Row $cols={2}>
            <Field>
              <span>{t('campaigns.content.invitationCourse', 'Course to invite to')}</span>
              <select
                value={draft?.invitation?.courseId ?? ''}
                onChange={(e) => updateOffer({ courseId: e.target.value })}
                style={{ padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14 }}
              >
                <option value=''>—</option>
                {MOCK_COURSES_FOR_FILTER.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {errors.invitationCourse && <FieldError>{errors.invitationCourse}</FieldError>}
            </Field>
            <Field>
              <span>{t('campaigns.content.expiry', 'Expires at')}</span>
              <Textinput
                type='date'
                value={draft?.invitation?.expiresAt?.slice(0, 10) ?? ''}
                onChange={(e) => updateOffer({ expiresAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
              />
              {errors.invitationExpiry && <FieldError>{errors.invitationExpiry}</FieldError>}
            </Field>
          </Row>
        </Section>
      )}

      {channels.includes(CHANNEL.EMAIL) && (
        <Section>
          <SectionTitle>
            <Icon.Email size='small' />
            {t('campaigns.content.emailContent', 'Email content')}
          </SectionTitle>

          {/* <Field>
            <span>{t('campaigns.content.template', 'Template')}</span>
            <TemplateGrid>
              {CAMPAIGN_TEMPLATES.filter((tpl) => TEMPLATE_VISUAL[tpl.id]).map((tpl) => (
                <TemplateCard key={tpl.id} $selected={draft?.template === tpl.id} type='button' onClick={() => setTemplate(tpl.id)}>
                  {TEMPLATE_VISUAL[tpl.id].icon}
                  {tpl.name}
                </TemplateCard>
              ))}
            </TemplateGrid>
          </Field> */}

          <Field>
            <span>{t('campaigns.content.subject', 'Subject line')}</span>
            <Textinput
              value={variant.subject ?? ''}
              onChange={(e) => updateVariant({ subject: e.target.value })}
              placeholder='Summer Sale — 30% off for the next 72 hours'
            />
            {errors.subject && <FieldError>{errors.subject}</FieldError>}
          </Field>

          <Field>
            <span>{t('campaigns.content.previewText', 'Preview text')}</span>
            <Textinput
              value={variant.previewText ?? ''}
              onChange={(e) => updateVariant({ previewText: e.target.value })}
              placeholder='Hand-picked courses, discounted for summer.'
            />
            {errors.previewText && <FieldError>{errors.previewText}</FieldError>}
          </Field>

          <Field>
            <span>{t('campaigns.content.body', 'Body')}</span>
            <TextArea value={variant.body ?? ''} onChange={(e) => updateVariant({ body: e.target.value })} placeholder='Write the email body here…' />
            {errors.body && <FieldError>{errors.body}</FieldError>}
          </Field>

          <Row>
            <Field>
              <span>{t('campaigns.content.ctaText', 'Call to action')}</span>
              <Textinput
                value={variant.callToAction?.text ?? ''}
                onChange={(e) => updateVariant({ callToAction: { ...(variant.callToAction ?? {}), text: e.target.value } })}
                placeholder='Shop the sale'
              />
            </Field>
            <Field>
              <span>{t('campaigns.content.ctaUrl', 'CTA URL')}</span>
              <Textinput
                value={variant.callToAction?.url ?? ''}
                onChange={(e) => updateVariant({ callToAction: { ...(variant.callToAction ?? {}), url: e.target.value } })}
                placeholder='https://school.coursio.com/sale'
              />
            </Field>
          </Row>
        </Section>
      )}

      {channels.includes(CHANNEL.NETWORK_UPSELL) && (
        <Section>
          <SectionTitle>
            <Icon.TrendingUp size='small' />
            {t('campaigns.content.inAppRecommendation', 'In-app recommendation')}
          </SectionTitle>

          {(() => {
            const discountScope = draft?.discount?.scopeCourseIds ?? [];

            if (offer === OFFER.INVITATION) {
              return (
                <Typo.SmallText color='textSecondary'>
                  {t('campaigns.content.inAppInvitationNote', 'The in-app card will feature the invitation course you picked above.')}
                </Typo.SmallText>
              );
            }
            if (offer === OFFER.DISCOUNT) {
              // Single source of truth: discount scope dictates the card.
              // Empty scope = store-wide discount, card just shows the offer badge.
              return (
                <Typo.SmallText color='textSecondary'>
                  {discountScope.length > 0
                    ? t(
                        'campaigns.content.inAppScopedDiscountNote',
                        'The in-app card will feature the {{n}} courses your discount applies to. Edit the list above in the discount section.',
                        { n: discountScope.length },
                      )
                    : t(
                        'campaigns.content.inAppStoreWideDiscountNote',
                        'Your discount applies to every course, so the in-app card will show the offer badge without listing specific courses.',
                      )}
                </Typo.SmallText>
              );
            }
            // No offer → the user picks which courses to feature.
            return (
              <>
                <Typo.SmallText color='textSecondary'>
                  {t('campaigns.content.inAppHint', 'Pick 1–{{max}} courses to feature on the student dashboard card.', { max: LIMITS.FEATURED_COURSES_MAX })}
                </Typo.SmallText>
                <Row>
                  {MOCK_COURSES_FOR_FILTER.map((c) => {
                    const checked = featured.includes(c.id);
                    return (
                      <label key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                        <input type='checkbox' checked={checked} onChange={() => toggleFeatured(c.id)} />
                        {c.name}
                      </label>
                    );
                  })}
                </Row>
                {errors.featuredCourses && <FieldError>{errors.featuredCourses}</FieldError>}
              </>
            );
          })()}

          {(featured.length > 0 || offer !== OFFER.NONE) && <UpsellCardPreview campaign={draft} theme={theme} />}
        </Section>
      )}
    </Card>
  );
};

export default StepContent;
